import type { ReactNode } from 'react'
import { LogoMark } from '../logo'
import { cn } from '../ui'

/**
 * Ürün çerçeveleri. Gerçek ekran görüntüsü değil, ürünün kendi token'larıyla
 * çizilmiş küçük sahneler: her temada keskin, her genişlikte okunaklı ve
 * hareket verilebilir. Gerçek ekranlar olgunlaştıkça yerini alabilir.
 */
export function Tarayici({
  adres,
  children,
  className,
  menu,
  aktif,
}: {
  adres: string
  children: ReactNode
  className?: string
  /** Sol menü öğeleri; verilmezse panel kabuğu çizilmez. */
  menu?: string[]
  aktif?: string
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[var(--radius-panel)] bg-surface text-ink ring-soft',
        className,
      )}
    >
      <div className="flex items-center gap-3 border-b border-line bg-subtle/60 px-3 py-2">
        <span className="flex gap-1.5" aria-hidden>
          <i className="size-2.5 rounded-full bg-line" />
          <i className="size-2.5 rounded-full bg-line" />
          <i className="size-2.5 rounded-full bg-line" />
        </span>
        <span className="data flex h-6 flex-1 items-center gap-1.5 rounded-[6px] bg-surface px-2 text-[10px] text-ink-3 ring-1 ring-line">
          <i className="size-1.5 rounded-full bg-success" aria-hidden />
          {adres}
        </span>
      </div>
      {menu ? (
        <div className="grid grid-cols-[104px_1fr] sm:grid-cols-[124px_1fr]">
          <aside className="hidden border-r border-line bg-subtle/40 p-2 sm:block">
            <div className="flex items-center gap-1.5 px-1.5 pb-2 pt-1 text-brand">
              <LogoMark boyut={14} />
              <span className="text-[10px] font-semibold text-ink">Panel</span>
            </div>
            <ul className="space-y-0.5">
              {menu.map((m) => (
                <li
                  key={m}
                  className={cn(
                    'rounded-[5px] px-1.5 py-1 text-[10px]',
                    m === aktif ? 'bg-brand-subtle font-medium text-brand' : 'text-ink-2',
                  )}
                >
                  {m}
                </li>
              ))}
            </ul>
          </aside>
          <div className="col-span-2 min-w-0 sm:col-span-1">{children}</div>
        </div>
      ) : (
        children
      )}
    </div>
  )
}

export const PANEL_MENU = [
  'Özet',
  'Üyeler',
  'Kurullar',
  'Aidat',
  'Gelir-gider',
  'İletişim',
  'Web sitesi',
  'Etkinlikler',
  'Ayarlar',
]

export function Telefon({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'relative w-[220px] overflow-hidden rounded-[32px] bg-ink p-2 shadow-[0_24px_48px_-20px_rgba(25,35,33,0.45)]',
        className,
      )}
    >
      <div
        className="absolute left-1/2 top-2 z-10 h-5 w-20 -translate-x-1/2 rounded-b-xl bg-ink"
        aria-hidden
      />
      <div className="overflow-hidden rounded-[24px] bg-page text-ink">{children}</div>
    </div>
  )
}

/** Küçük kimlik: baş harfli yuvarlak. */
export function Avatar({ ad, className }: { ad: string; className?: string }) {
  const harf = ad
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
  return (
    <span
      className={cn(
        'grid size-6 shrink-0 place-items-center rounded-full bg-brand-subtle text-[9px] font-semibold text-brand',
        className,
      )}
      aria-hidden
    >
      {harf}
    </span>
  )
}

export function Etiket({
  tone,
  children,
}: {
  tone: 'success' | 'warning' | 'info' | 'neutral' | 'danger'
  children: ReactNode
}) {
  const t = {
    success: 'bg-success/12 text-success',
    warning: 'bg-warning/12 text-warning',
    info: 'bg-info/12 text-info',
    neutral: 'bg-subtle text-ink-2',
    danger: 'bg-danger/12 text-danger',
  }[tone]
  return (
    <span className={cn('rounded-full px-1.5 py-px text-[9px] font-medium', t)}>{children}</span>
  )
}

export function Buton({ children, ikincil }: { children: ReactNode; ikincil?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center rounded-[5px] px-2 text-[10px] font-medium',
        ikincil ? 'bg-surface text-ink ring-1 ring-line' : 'bg-brand text-on-brand',
      )}
    >
      {children}
    </span>
  )
}
